import { Contractor } from "./data";
import {
  BadgeCheck,
  BriefcaseBusiness,
  Building2,
  CalendarClock,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { ImageWithFallback } from "components/ui/ImageWithFallback";

const relibuildIcon = "/relibuild-favicon.ico";

interface ContractorDetailModalProps {
  contractor: any;
  isSelected: boolean;
  onToggleSelect: () => void;
  onClose: () => void;
}

export function ContractorDetailModal({
  contractor,
  isSelected,
  onToggleSelect,
  onClose,
}: ContractorDetailModalProps) {
  return (
    <div
    style={{
      zIndex:1000000
    }} 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${contractor.name || "Contractor"} details`}
    >
      <div
  
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 border-b border-gray-200 bg-white/95 px-5 py-4 backdrop-blur">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {/* ✅ Avatar fallback */}
              <div className="flex h-20 w-20 flex-none items-center justify-center overflow-hidden rounded-2xl bg-white p-3 ring-1 ring-gray-200">
                <ImageWithFallback
                  src={contractor.logo || relibuildIcon}
                  fallbackSrc={relibuildIcon}
                  alt={`${contractor.name || "Contractor"} logo`}
                  className="h-full w-full object-contain"
                />
              </div>

              <div>
                <div className=" flex flex-wrap items-center gap-2">
                  <div className="text-xl font-semibold text-gray-900">{contractor.name}</div>
                  {contractor.isVerified && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                      <ShieldCheck className="h-3.5 w-3.5" />
                      Verified
                    </span>
                  )}
                </div>

                {/* ✅ Profile */}
                {contractor.profile && (
                  <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
                    <Sparkles className="h-4 w-4" />
                    {contractor.profile}
                  </div>
                )}

                {/* ✅ Company */}
                {contractor.companyName && (
                  <div className="mt-3 inline-flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
                    <Building2 className="h-4 w-4" />
                    {contractor.companyName}
                  </div>
                )}

                <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
                  <MapPin className="h-4 w-4" />
                  {contractor.location}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-transparent border-0 px-3 py-1 hover:bg-gray-100"
              aria-label="Close contractor details"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-5">
          {/* Stats */}
          <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3.5">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Completed Jobs
              </div>
              <div className="text-lg font-semibold text-gray-900">
                {contractor.completedJobs || 0}
              </div>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3.5">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Experience
              </div>
              <div className="text-lg font-semibold text-gray-900">
                {contractor.yearsExperience || 0} yrs
              </div>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3.5">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Availability
              </div>
              <div className="text-sm font-medium text-gray-900">
                {contractor.availability || "Not specified"}
              </div>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3.5">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Price Range
              </div>
              <div className="text-lg font-semibold text-gray-900">
                {contractor.priceRange || "-"}
              </div>
            </div>
          </div>

          <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-2xl border border-gray-100 bg-white p-4">
              <div className="mb-3 text-sm font-semibold text-gray-900">Business Snapshot</div>
              <div className="space-y-3 text-sm text-gray-700">
                {contractor.companyName && (
                  <div className="flex items-center gap-3">
                    <Building2 className="h-4 w-4 text-amber-600" />
                    <span>{contractor.companyName}</span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <BriefcaseBusiness className="h-4 w-4 text-blue-600" />
                  <span>{contractor.profile || "General contractor profile"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-rose-500" />
                  <span>{contractor.location || "Location not provided"}</span>
                </div>
                {contractor.availability && (
                  <div className="flex items-center gap-3">
                    <CalendarClock className="h-4 w-4 text-emerald-600" />
                    <span>{contractor.availability}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-4">
              <div className="mb-3 text-sm font-semibold text-gray-900">Contact Details</div>
              <div className="space-y-3 text-sm text-gray-700">
                {contractor.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="break-all">{contractor.email}</span>
                  </div>
                )}
                {contractor.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span>{contractor.phone}</span>
                  </div>
                )}
                {!contractor.email && !contractor.phone && (
                  <div className="text-sm text-gray-500">Contact information not provided</div>
                )}
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="mb-5">
            <div className="mb-3 text-sm font-semibold text-gray-900">Services</div>
            <div className="flex flex-wrap gap-2">
              {contractor.profile && (
                <span className="rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
                  {contractor.profile}
                </span>
              )}

              {contractor.services.map((service: any, index: number) => {
                const serviceLabel =
                  typeof service === "string"
                    ? service
                    : service?.label || service?.name || "Service";

                return (
                <span
                  key={`${contractor.id}-detail-service-${index}`}
                  className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700"
                >
                  {serviceLabel}
                </span>
              )})}
            </div>
          </div>

          {/* About */}
          <div className="mb-5 rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <div className="mb-2 text-sm font-semibold text-gray-900">About</div>
            <div className="text-sm leading-6 text-gray-700">
              {contractor.description || "No description available."}
            </div>
          </div>

          {/* Portfolio */}
          {contractor.portfolioImages?.length > 0 && (
            <div className="mb-6">
              <div className="mb-3 text-sm font-semibold text-gray-900">Portfolio</div>
              <div className="grid grid-cols-2 gap-3">
                {contractor.portfolioImages.map((img: string, i: number) => (
                  <img
                    key={i}
                    src={img}
                    alt={`${contractor.name || "Contractor"} portfolio ${i + 1}`}
                    className="h-40 w-full rounded-lg object-cover"
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border-t bg-white px-5 py-3.5">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-200 bg-transparent px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>

            <button
              type="button"
              onClick={() => {
                onToggleSelect();
                onClose();
              }}
              className={`rounded-xl px-6 py-2.5 text-sm font-medium transition ${
                isSelected
                  ? "bg-gray-200 text-gray-800"
                  : "contractor-primary-action border shadow-sm"
              }`}
            >
              {isSelected ? "Remove" : "Select Contractor"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
